'use client';

import { useTranslations } from 'next-intl';
import { ClipboardCheck, Eye } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OcrQaListStatusBadge } from './ocr-qa-list-status-badge';
import type { OcrQaContainerListItem, OcrQaTier } from '../types';

const TIER_DETAIL_ROUTE: Record<OcrQaTier, string> = {
  1: '/dashboard/qa-ocr-1',
  2: '/dashboard/qa-ocr-2',
  3: '/dashboard/qa-ocr-3'
};

interface OcrQaContainerCompactRowProps {
  item: OcrQaContainerListItem;
  tier: OcrQaTier;
}

export function OcrQaContainerCompactRow({
  item,
  tier
}: OcrQaContainerCompactRowProps) {
  const t = useTranslations('ocrQa');

  const detailHref = `${TIER_DETAIL_ROUTE[tier]}/${item.container_id}`;
  const oldestPendingDisplay = item.oldest_pending_at
    ? new Date(item.oldest_pending_at).toLocaleString()
    : '—';

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md'>
        <ClipboardCheck className='text-primary h-3.5 w-3.5' />
      </div>

      <div className='min-w-0 flex-1'>
        <Link
          href={detailHref}
          className='hover:text-primary truncate font-medium hover:underline'
        >
          {item.container_number}
        </Link>
      </div>

      <OcrQaListStatusBadge status={item.list_status} className='shrink-0' />

      <div className='flex shrink-0 items-center gap-2'>
        <Badge
          variant='outline'
          className='gap-1'
          title={t('columnPagesPending')}
        >
          {item.pending_count}
        </Badge>
        <Badge
          variant='outline'
          className='gap-1'
          title={t('columnPagesInProgress')}
        >
          {item.in_progress_count}
        </Badge>
      </div>

      <span
        className='text-muted-foreground hidden truncate text-xs sm:inline-block'
        title={t('columnOldestPending')}
      >
        {oldestPendingDisplay}
      </span>

      <div className='flex shrink-0 items-center gap-1'>
        <Button asChild variant='ghost' size='sm' className='h-7'>
          <Link href={detailHref}>
            <Eye className='me-1.5 h-3.5 w-3.5' />
            {t('open')}
          </Link>
        </Button>
      </div>
    </div>
  );
}
